import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { SET_VIDEO_POSTER } from '../../graphql';
import { formatDuration, type VideoInfo } from '../../lib/media/uploadVideo';

/**
 * A video's cover — what shows before someone presses play: a moment in the
 * video (picked with a slider, previewed live) or any image in Media. Pages
 * and articles placed from now on use it; ones already saved keep theirs until
 * they're saved (and approved) again, like swapping an image.
 */

export interface CoverImage {
  id: string;
  name: string;
  url: string;
}

export function VideoCoverDialog({
  accountId,
  assetId,
  name,
  video,
  images,
  onClose,
  onSaved,
}: {
  accountId: string;
  assetId: string;
  name: string;
  video: VideoInfo;
  images: CoverImage[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const duration = Math.max(0, video.durationSeconds || 0);
  const [mode, setMode] = useState<'moment' | 'image'>(video.posterAssetId ? 'image' : 'moment');
  const [time, setTime] = useState<number>(video.posterTimeSeconds ?? Math.min(1, duration));
  const [shownTime, setShownTime] = useState(time);
  const [imageId, setImageId] = useState<string | null>(video.posterAssetId ?? null);
  const [error, setError] = useState<string | null>(null);
  const [save, { loading }] = useMutation(SET_VIDEO_POSTER);

  // Ask for the frame only once the slider settles.
  useEffect(() => {
    const t = setTimeout(() => setShownTime(time), 250);
    return () => clearTimeout(t);
  }, [time]);

  const frame = video.frameUrl
    ? `${video.frameUrl}?time=${shownTime.toFixed(1)}s&height=360`
    : null;

  const apply = async (vars: { imageAssetId?: string | null; timeSeconds?: number | null }) => {
    setError(null);
    try {
      await save({
        variables: {
          accountId,
          assetId,
          imageAssetId: vars.imageAssetId ?? null,
          timeSeconds: vars.timeSeconds ?? null,
        },
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn’t save the cover');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto p-5 space-y-4 bg-cmp-raise border border-cmp-line text-cmp-ink shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-cmp-ink">Cover for {name}</h2>
            <p className="text-xs text-cmp-ink-2 mt-1">
              What shows before someone presses play. Pages and articles that already have this
              video keep their cover until they’re next saved and approved.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          {(
            [
              ['moment', 'A moment in the video'],
              ['image', 'An image from Media'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className="ink-chip"
              data-active={mode === id ? 'true' : 'false'}
              onClick={() => setMode(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'moment' ? (
          <div className="space-y-3">
            <div
              className="bg-black rounded overflow-hidden flex items-center justify-center mx-auto"
              style={{
                // The video's own shape, never taller than half the screen.
                aspectRatio: `${video.width || 16} / ${video.height || 9}`,
                height: 'min(45vh, 340px)',
                maxWidth: '100%',
              }}
            >
              {frame ? (
                <img
                  src={frame}
                  alt={`Frame at ${formatDuration(shownTime)}`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <span
                  className="text-sm px-4 text-center"
                  style={{ color: 'rgba(255,255,255,0.75)' }}
                >
                  Preview appears once the video is ready
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={duration || 0}
                step={0.5}
                value={time}
                onChange={(e) => setTime(Number(e.target.value))}
                className="flex-1"
                aria-label="Moment in the video"
                disabled={!duration}
              />
              <span className="text-sm tabular-nums text-cmp-ink-2 w-16 text-right">
                {formatDuration(time)}
              </span>
            </div>
            <Button
              disabled={loading || !duration}
              onClick={() => void apply({ timeSeconds: time })}
            >
              Use this moment
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {images.length === 0 ? (
              <p className="text-sm text-cmp-ink-2">
                No images in Media yet. Upload one, then choose it here.
              </p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {images.map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setImageId(img.id)}
                    title={img.name}
                    className="rounded overflow-hidden border-2 bg-cmp-surface"
                    style={{
                      aspectRatio: '16 / 9',
                      borderColor: imageId === img.id ? 'var(--ink-accent)' : 'transparent',
                    }}
                  >
                    <img
                      src={img.url}
                      alt={img.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
            <Button
              disabled={loading || !imageId}
              onClick={() => void apply({ imageAssetId: imageId })}
            >
              Use this image
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-cmp-line">
          <button
            type="button"
            className="text-xs text-cmp-ink-2 hover:text-cmp-ink underline"
            disabled={loading}
            onClick={() => void apply({})}
          >
            Use the video’s own first frame
          </button>
          {error && <span className="text-xs text-red-500">{error}</span>}
        </div>
      </Card>
    </div>
  );
}
